import React, { useEffect, useRef, useState } from 'react';
import { array, bool, func, object, oneOf, shape, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { getListingCompleteness } from '../../util/listingCompleteness';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  MAX_LISTING_IMAGES,
  MAX_PACKAGES,
} from '../../util/listingPackages';
import { richTextToText } from '../../util/validators';
import {
  EditListingFaqSection,
  EditListingPackagesSection,
  Form,
  ListingProgressCard,
  PrimaryButton,
  RelatedListingsPicker,
  UnsavedInfoPrompt,
} from '../../components';
import { isDraftDirty } from '../../components/EditableItemList/EditableItemList';
import { EMPTY_PACKAGE } from '../../components/EditListingPackagesSection/EditListingPackagesSection';
import { EMPTY_FAQ_ITEM } from '../../components/EditListingFaqSection/EditListingFaqSection';
import {
  KIND_SERVICES,
  KIND_WORKS,
} from '../../components/RelatedListingsPicker/RelatedListingsPicker';
import BasicInfoSection from './BasicInfoSection';

import css from './EditServiceListingForm.css';

const LISTING_STATE_PUBLISHED = 'published';

// The listing-level fields. A draft may leave them empty or the description
// short, so their "required" and "too short" errors don't stop a save; any
// other error (too long, a link or contact details in the description) does.
const LISTING_FIELDS = ['title', 'description'];

// Quill may rewrite stored markup and whitespace when it loads a value, so a
// field counts as unchanged while its visible text is.
const visibleText = html => richTextToText(html).replace(/\s+/g, '');
const isTextUnchanged = (value, initialValue) => visibleText(value) === visibleText(initialValue);

// The item editors of the pricing and FAQ sections. An open editor keeps its
// item in `draftName` until it is saved into `name`.
const ITEM_EDITORS = [
  { section: 'pricing', name: 'packages', draftName: 'packageDraft', emptyItem: EMPTY_PACKAGE },
  { section: 'faq', name: 'faq', draftName: 'faqDraft', emptyItem: EMPTY_FAQ_ITEM },
];

// Final Form drops a cleared field's key and Quill rewrites what it loads, so
// a missing value counts as empty text and text compares by what's visible.
const isText = value => value === undefined || value === null || typeof value === 'string';
const isSameValue = (value, initialValue) => {
  if (isText(value) && isText(initialValue)) {
    return isTextUnchanged(value, initialValue);
  }
  if (Array.isArray(value) && Array.isArray(initialValue)) {
    return (
      value.length === initialValue.length &&
      value.every((item, i) => isSameValue(item, initialValue[i]))
    );
  }
  if (isPlainObject(value) && isPlainObject(initialValue)) {
    const keys = Object.keys({ ...value, ...initialValue });
    return keys.every(key => isSameValue(value[key], initialValue[key]));
  }
  return isEqual(value, initialValue);
};

// Images live outside the form values, and an open item editor counts only
// once it has changes.
const NOT_COMPARED = ['images', ...ITEM_EDITORS.map(editor => editor.draftName)];
const imageIds = images => (images || []).map(image => image.id);

const hasUnsavedChanges = (values, initialValues, images) =>
  ITEM_EDITORS.some(editor =>
    isDraftDirty(values[editor.draftName], values[editor.name], editor.emptyItem)
  ) ||
  !isSameValue(omit(values, NOT_COMPARED), omit(initialValues, NOT_COMPARED)) ||
  !isEqual(imageIds(images), imageIds(initialValues.images));

const saveLabelId = (listingState, isComplete) => {
  if (listingState === LISTING_STATE_PUBLISHED) {
    return 'EditServiceListingForm.publishEdits';
  }
  return isComplete
    ? 'EditServiceListingForm.saveAndPreview'
    : 'EditServiceListingForm.saveAsDraft';
};

const scrollIntoView = element => {
  if (element && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const EditServiceListingFormContent = props => {
  const {
    className,
    form,
    values,
    initialValues,
    images,
    listingState,
    saveInProgress,
    imageUploadInProgress,
    imageUploadError,
    saveError,
    onSave,
    onImageUpload,
    onRemoveImage,
    onManageDisableScrolling,
    relatedOptions,
    intl,
  } = props;
  const sectionRefs = { basic: useRef(null), pricing: useRef(null), faq: useRef(null) };
  const [unsavedSection, setUnsavedSection] = useState(null);

  // The message goes away once the editor it points at is saved or discarded
  const unsavedEditor = ITEM_EDITORS.find(editor => editor.section === unsavedSection);
  const unsavedDraftIsOpen = !!unsavedEditor && !!values[unsavedEditor.draftName];
  useEffect(() => {
    if (unsavedSection && !unsavedDraftIsOpen) {
      setUnsavedSection(null);
    }
  }, [unsavedSection, unsavedDraftIsOpen]);

  // A successful save leaves the page, so the leave warning stands down while
  // a save is on its way and comes back if it fails.
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (saveError) {
      setIsSaving(false);
    }
  }, [saveError]);
  const warnBeforeLeaving = !isSaving && hasUnsavedChanges(values, initialValues, images);

  // The browser's own warning on reload or closing the tab; UnsavedInfoPrompt
  // covers navigation inside the app.
  useEffect(() => {
    if (!warnBeforeLeaving) {
      return undefined;
    }
    const onBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [warnBeforeLeaving]);

  const requiredMessage = intl.formatMessage({ id: 'EditServiceListingForm.required' });
  const descriptionTooLongMessage = intl.formatMessage(
    { id: 'EditServiceListingForm.tooLong' },
    { max: DESCRIPTION_MAX_LENGTH }
  );
  const descriptionTooShortMessage = intl.formatMessage(
    { id: 'EditServiceListingForm.tooShort' },
    { min: DESCRIPTION_MIN_LENGTH }
  );
  const isPublished = listingState === LISTING_STATE_PUBLISHED;
  const completeness = getListingCompleteness(
    { ...values, images },
    {
      isLegacyDescription:
        isPublished && isTextUnchanged(values.description, initialValues.description),
    }
  );
  // Old listings can have more packages or images than allowed; the sections
  // show why, and no save goes through until they are trimmed.
  const isOverLimit =
    (values.packages || []).length > MAX_PACKAGES || images.length > MAX_LISTING_IMAGES;
  const saveDisabled =
    !(values.title || '').trim() ||
    (isPublished && !completeness.isComplete) ||
    isOverLimit ||
    imageUploadInProgress ||
    saveInProgress;
  const saveLabel = <FormattedMessage id={saveLabelId(listingState, completeness.isComplete)} />;
  const saveErrorMessage = saveError ? (
    <FormattedMessage id="EditServiceListingForm.saveFailed" />
  ) : null;

  // Saving bypasses Final Form's submit, which would refuse while any field is
  // invalid: a draft may be incomplete, and an open item editor registers
  // validators of its own.
  const handleSubmit = event => {
    event.preventDefault();
    if (saveDisabled) {
      return;
    }
    const { values: currentValues, errors = {} } = form.getState();

    const dirtyEditor = ITEM_EDITORS.find(editor =>
      isDraftDirty(currentValues[editor.draftName], currentValues[editor.name], editor.emptyItem)
    );
    if (dirtyEditor) {
      setUnsavedSection(dirtyEditor.section);
      scrollIntoView(sectionRefs[dirtyEditor.section].current);
      return;
    }

    // An old listing's description can be over today's limit. Left as it
    // was, it doesn't stop the save; once the user changes it, it does.
    const isUnchangedLegacyTooLong = name =>
      name === 'description' &&
      errors[name] === descriptionTooLongMessage &&
      isTextUnchanged(currentValues[name], initialValues[name]);
    const notDoneMessages = [requiredMessage, descriptionTooShortMessage];
    const blockingFields = LISTING_FIELDS.filter(
      name =>
        errors[name] && !notDoneMessages.includes(errors[name]) && !isUnchangedLegacyTooLong(name)
    );
    if (blockingFields.length > 0) {
      form.batch(() => blockingFields.forEach(name => form.blur(name)));
      scrollIntoView(sectionRefs.basic.current);
      return;
    }

    // An open editor without changes is closed silently
    form.batch(() =>
      ITEM_EDITORS.forEach(editor => {
        if (currentValues[editor.draftName]) {
          form.change(editor.draftName, null);
        }
      })
    );
    setIsSaving(true);
    onSave({ ...currentValues, images, packageDraft: null, faqDraft: null });
  };

  const unsavedMessage = section =>
    unsavedSection === section ? (
      <p className={css.unsavedEditor} role="alert">
        <FormattedMessage id="EditServiceListingForm.saveOpenEditorFirst" />
      </p>
    ) : null;

  return (
    <Form className={classNames(css.root, className)} onSubmit={handleSubmit}>
      <UnsavedInfoPrompt when={warnBeforeLeaving} />
      <h1 className={css.pageTitle}>
        <FormattedMessage
          id={isPublished ? 'EditServiceListingForm.titleEdit' : 'EditServiceListingForm.titleNew'}
        />
      </h1>
      <div className={css.content}>
        <div className={css.formColumn}>
          <div ref={sectionRefs.basic} className={css.sectionAnchor}>
            <BasicInfoSection
              requiredMessage={requiredMessage}
              images={images}
              imageUploadError={imageUploadError}
              onImageUpload={onImageUpload}
              onRemoveImage={onRemoveImage}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div ref={sectionRefs.pricing} className={css.sectionAnchor}>
            {unsavedMessage('pricing')}
            <EditListingPackagesSection
              onPackageSaved={() => scrollIntoView(sectionRefs.pricing.current)}
            />
          </div>
          <RelatedListingsPicker
            name="relatedWorkIds"
            kind={KIND_WORKS}
            items={relatedOptions.works}
            status={relatedOptions.status}
          />
          <RelatedListingsPicker
            name="relatedServiceIds"
            kind={KIND_SERVICES}
            items={relatedOptions.services}
            status={relatedOptions.status}
          />
          <div ref={sectionRefs.faq} className={css.sectionAnchor}>
            {unsavedMessage('faq')}
            <EditListingFaqSection />
          </div>
          <div className={css.bottomActions}>
            {saveErrorMessage ? (
              <p className={css.saveError} role="alert">
                {saveErrorMessage}
              </p>
            ) : null}
            <PrimaryButton
              type="submit"
              rootClassName={css.saveButton}
              disabled={saveDisabled}
              inProgress={saveInProgress}
            >
              {saveLabel}
            </PrimaryButton>
          </div>
        </div>
        <ListingProgressCard
          className={css.progressCard}
          completeness={completeness}
          saveLabel={saveLabel}
          saveDisabled={saveDisabled}
          saveInProgress={saveInProgress}
          errorMessage={saveErrorMessage}
        />
      </div>
    </Form>
  );
};

const noop = () => null;

/**
 * The single-page service editor: basic information, pricing packages, the
 * related works and services pickers and the FAQ beside the progress card.
 * Values are { title, images, description, packages, faq,
 * packageDraft, faqDraft, relatedWorkIds, relatedServiceIds };
 * onSubmit gets them with the drafts closed and `images` from the images prop.
 */
const EditServiceListingFormComponent = props => {
  const { initialValues, onSubmit, ...rest } = props;
  return (
    <FinalForm
      initialValues={initialValues}
      // The page builds initialValues on every render; re-initialising would
      // drop unsaved edits and could strand an open item editor.
      initialValuesEqual={isEqual}
      mutators={{ ...arrayMutators }}
      // Saving goes through EditServiceListingFormContent's handleSubmit
      onSubmit={noop}
      render={({ form, values, initialValues: formInitialValues }) => (
        <EditServiceListingFormContent
          {...rest}
          form={form}
          values={values}
          initialValues={formInitialValues}
          onSave={onSubmit}
        />
      )}
    />
  );
};

EditServiceListingFormComponent.defaultProps = {
  className: null,
  images: [],
  saveInProgress: false,
  imageUploadInProgress: false,
  imageUploadError: null,
  saveError: null,
  relatedOptions: { status: 'loading', works: [], services: [] },
};

EditServiceListingFormComponent.propTypes = {
  className: string,
  initialValues: object.isRequired,
  onSubmit: func.isRequired,
  // Saved images and uploads ({ id, imageId, file }), as the page builds them
  images: array,
  onImageUpload: func.isRequired,
  onRemoveImage: func.isRequired,
  listingState: oneOf(['new', 'draft', LISTING_STATE_PUBLISHED]).isRequired,
  saveInProgress: bool,
  // Saving waits for running uploads: they have no image id yet
  imageUploadInProgress: bool,
  imageUploadError: propTypes.error,
  saveError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  // The author's pickable listings (EditListingPage.duck getRelatedPickerOptions)
  relatedOptions: shape({
    status: oneOf(['loading', 'loaded', 'error']).isRequired,
    works: array.isRequired,
    services: array.isRequired,
  }),
  intl: intlShape.isRequired,
};

export default injectIntl(EditServiceListingFormComponent);
